import AppLayout from "components/AppLayout";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import Assets from "./Assets";
import Login from "./Login";
import Pickups from "./Pickups";
import CompanySettings from "./CompanySettings";
import UserManagement from "./CompanySettings/Users/UserManagement";
import NewUser from "./CompanySettings/Users/New";
import Users from "./CompanySettings/Users";
import EditUser from "./CompanySettings/Users/Edit";
import Security from "./CompanySettings/Security";
import Support from "./Support";
import MagicLinkSignin from "./MagicLinkSignin";
import NewPickup from "./Pickups/PickupNew";
import EditPickup from "./Pickups/PickupEdit";
import PickupTracking from "./Pickups/PickupTracking";
import CustomerAssetBreakdown from './Pickups/CustomerAssetBreakdown';
import SustainabilityReport from './Pickups/SustainabilityReport';
import NoBackendConnection from "components/NoBackendConnection";
import CheckEmail from "./CheckEmail";
import SetPassword from "./SetPassword";
import Documents from './Documents';
import WorkOrders from './WorkOrders';
import WorkOrderNew from './WorkOrders/WorkOrderNew';
import WorkOrderEdit from './WorkOrders/WorkOrderEdit';
import useAuthentication from "hooks/useAuthentication";
import HomeRedirect from "./HomeRedirect";
import AssettechOnly from "components/AssettechOnly";
import Dashboard from "./Dashboard";
import LocationsList from "./Locations";
import LocationNew from "./Locations/LocationNew";
import LocationEdit from "./Locations/LocationEdit";
import EmployeesList from "./Employees";
import EmployeeNew from "./Employees/EmployeeNew";
import EmployeeEdit from "./Employees/EmployeeEdit";
import AssetDetail from "./Assets/AssetDetail";
import AssetNew from "./Assets/AssetNew";
import AssetUuidRedirect from "./Assets/AssetUuidRedirect";

const PUBLIC_PATHS = [
  "/login",
  "/check-email",
  "/no-backend-connection",
] as const;

function isPublicPath(pathname: string): boolean {
  if (PUBLIC_PATHS.some((p) => pathname === p)) return true;
  if (pathname === "/support") return true;
  if (pathname.startsWith("/support/")) return true;
  if (pathname.startsWith("/reset-password/")) return true;
  if (pathname.startsWith("/magic-link-signin/")) return true;
  return false;
}

function ProtectedRoutes() {
  const { pathname } = useLocation();
  const { loggedIn } = useAuthentication();

  if (!loggedIn && !isPublicPath(pathname)) {
    return <Navigate to="/login" replace />;
  }

  return (
    <Routes>
      <Route path='/support' element={<Support />} />
      <Route path='/support/:subdomain/:token' element={<Support />} />
      <Route path='/support/:token' element={<Support />} />
      <Route path='/check-email' element={<CheckEmail />} />
      <Route path='/reset-password/:token' element={<SetPassword />} />
      <Route path='/no-backend-connection' element={<NoBackendConnection />} />
      <Route path='/magic-link-signin/:token' element={<MagicLinkSignin />} />
      <Route path='/login' element={<Login />} />
      <Route path='/' element={<HomeRedirect />} />
      <Route
        path='/dashboard'
        element={
          <AppLayout>
            <AssettechOnly>
              <Dashboard />
            </AssettechOnly>
          </AppLayout>
        }
      />
      <Route
        path='/locations'
        element={
          <AppLayout>
            <AssettechOnly>
              <LocationsList />
            </AssettechOnly>
          </AppLayout>
        }
      />
      <Route
        path='/locations/new'
        element={
          <AppLayout container>
            <AssettechOnly>
              <LocationNew />
            </AssettechOnly>
          </AppLayout>
        }
      />
      <Route
        path='/locations/:uuid/edit'
        element={
          <AppLayout container>
            <AssettechOnly>
              <LocationEdit />
            </AssettechOnly>
          </AppLayout>
        }
      />
      <Route
        path='/employees'
        element={
          <AppLayout>
            <AssettechOnly>
              <EmployeesList />
            </AssettechOnly>
          </AppLayout>
        }
      />
      <Route
        path='/employees/new'
        element={
          <AppLayout container>
            <AssettechOnly>
              <EmployeeNew />
            </AssettechOnly>
          </AppLayout>
        }
      />
      <Route
        path='/employees/:uuid/edit'
        element={
          <AppLayout container>
            <AssettechOnly>
              <EmployeeEdit />
            </AssettechOnly>
          </AppLayout>
        }
      />
      {/* <Route
        path="/login"
        element={
          <AppLayout>
            <Login />
          </AppLayout>
        }
      /> */}
      <Route
        path='/pickups'
        element={
          <AppLayout>
            <Pickups />
          </AppLayout>
        }
      />
      <Route
        path='/pickups/new'
        element={
          <AppLayout container>
            <NewPickup />
          </AppLayout>
        }
      />
      <Route
        path='/pickups/:pickupUuid/edit'
        element={
          <AppLayout container>
            <EditPickup />
          </AppLayout>
        }
      />
      <Route
        path='/pickups/:pickupUuid/cab'
        element={
          <AssettechOnly>
            <CustomerAssetBreakdown />
          </AssettechOnly>
        }
      />
      <Route
        path='/pickups/:pickupUuid/sustainability'
        element={
          <AssettechOnly>
            <SustainabilityReport />
          </AssettechOnly>
        }
      />
      <Route
        path='/pickups/:pickupUuid/tracking'
        element={
          <AppLayout container>
            <PickupTracking />
          </AppLayout>
        }
      />
      <Route
        path='/assets/new'
        element={
          <AppLayout container>
            <AssettechOnly>
              <AssetNew />
            </AssettechOnly>
          </AppLayout>
        }
      />
      <Route
        path='/assets/:uuid/edit'
        element={
          <AppLayout container>
            <AssettechOnly>
              <AssetDetail />
            </AssettechOnly>
          </AppLayout>
        }
      />
      <Route
        path='/assets/:uuid'
        element={
          <AppLayout container>
            <AssettechOnly>
              <AssetUuidRedirect />
            </AssettechOnly>
          </AppLayout>
        }
      />
      <Route
        path='/assets'
        element={
          <AppLayout>
            <Assets />
          </AppLayout>
        }
      />
      <Route
        path='/documents'
        element={
          <AppLayout>
            <Documents />
          </AppLayout>
        }
      />
      <Route
        path='/work_orders'
        element={
          <AppLayout>
            <WorkOrders />
          </AppLayout>
        }
      />
      <Route
        path='/work_orders/new'
        element={
          <AppLayout container>
            <WorkOrderNew />
          </AppLayout>
        }
      />
      <Route
        path='/work_orders/:uuid/edit'
        element={
          <AppLayout container>
            <WorkOrderEdit />
          </AppLayout>
        }
      />
      <Route
        path='/company-settings'
        element={
          <AppLayout noPadding>
            <CompanySettings />
          </AppLayout>
        }
      >
        <Route
          index
          element={<Navigate to='/company-settings/users' replace />}
        />
        <Route path='users' element={<Users />}>
          <Route index element={<UserManagement />} />
          <Route path='new' element={<NewUser />} />
          <Route path=':uuid/edit' element={<EditUser />} />
        </Route>
        <Route path='security' element={<Security />} />
      </Route>

      <Route
        path='*'
        element={
          <AppLayout>
            <Pickups />
          </AppLayout>
        }
      />
    </Routes>
  );
}

export default function Pages() {
  return <ProtectedRoutes />;
}
